const MODE_EX = 'ex';
const MODE_INC = 'inc';
const ATTR_VAT_DISPLAY = 'data-vat-display';

const parseRate = (value) => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return null;
};

/** Resolve VAT % for the visitor's market country, else theme default. */
export const resolveRatePercent = (vat) => {
  const fallback = parseRate(vat?.ratePercent) ?? 0;
  const code = vat?.countryCode;
  const rates = vat?.countryRates;

  if (!code || !rates || typeof rates !== 'object') {
    return fallback;
  }

  const marketRate = parseRate(rates[code]);
  return marketRate !== null ? marketRate : fallback;
};

export const readVatConfig = () => {
  const v = window.theme?.vat;
  if (!v?.enabled) return null;

  return {
    ratePercent: resolveRatePercent(v),
    basis: v.basis,
  };
};

const addVat = (exCents, ratePercent) =>
  Math.round((exCents * (100 + ratePercent)) / 100);

/** VAT % resolved for the visitor's market (0 when disabled/untaxed). */
export const getMarketRatePercent = () => {
  const cfg = readVatConfig();
  return cfg ? cfg.ratePercent : 0;
};

/**
 * Tax portion of a canonical amount for the current market rate.
 * Constant regardless of the inc/ex display toggle.
 * @param {number} canonicalCents
 * @returns {number}
 */
export const getVatCents = (canonicalCents) => {
  const cfg = readVatConfig();
  if (!cfg || typeof canonicalCents !== 'number' || Number.isNaN(canonicalCents)) {
    return 0;
  }
  const { ratePercent, basis } = cfg;
  if (ratePercent <= 0) return 0;

  if (basis === 'inclusive') {
    const inc = canonicalCents;
    const ex = Math.round((inc * 100) / (100 + ratePercent));
    return inc - ex;
  }

  const ex = canonicalCents;
  return addVat(ex, ratePercent) - ex;
};

/**
 * @param {number} canonicalCents - Shopify money value (variant.price etc.)
 * @returns {number}
 */
export const getDisplayCents = (canonicalCents) => {
  const cfg = readVatConfig();
  if (!cfg || typeof canonicalCents !== 'number' || Number.isNaN(canonicalCents)) {
    return canonicalCents;
  }
  const mode =
    document.documentElement.getAttribute(ATTR_VAT_DISPLAY) ||
    (window.theme?.vat?.defaultMode === MODE_EX ? MODE_EX : MODE_INC);
  const { ratePercent } = cfg;

  if (ratePercent <= 0) {
    return canonicalCents;
  }

  if (cfg.basis === 'inclusive') {
    const inc = canonicalCents;
    const ex = Math.round((canonicalCents * 100) / (100 + ratePercent));
    return mode === MODE_EX ? ex : inc;
  }

  const ex = canonicalCents;
  const inc = addVat(ex, ratePercent);
  return mode === MODE_EX ? ex : inc;
};

/**
 * Savings in the same “display” basis as compare-at vs sale price (per-line VAT rounding).
 */
export const getDisplaySavingsCents = (compareAtCents, salePriceCents) => {
  const dc = getDisplayCents(compareAtCents);
  const ds = getDisplayCents(salePriceCents);
  return Math.max(0, dc - ds);
};

export const getDisplaySavingsPercent = (compareAtCents, salePriceCents) => {
  const dc = getDisplayCents(compareAtCents);
  if (dc <= 0) return 0;
  const diff = getDisplaySavingsCents(compareAtCents, salePriceCents);
  return Math.round((diff * 100) / dc);
};
